import { useDrag } from '@use-gesture/react';
import { useState } from 'react';
import { animated, config, useSpring } from 'react-spring';
import { useMeasure } from 'src/lib/player/utils/hooks';

export const CLOSED_HEIGHT = 26;
export const MIN_OPEN_HEIGHT = 230;
export const HIGH_HEIGHT = 300;
export const MAX_HEIGHT_MARGIN = 30;

export default function Sheet({
  maxHeight,
  children,
  isInitiallyOpen = false,
}: {
  maxHeight: number;
  children: (width: number, height: number) => JSX.Element;
  isInitiallyOpen: boolean;
}): JSX.Element {
  const [contentRef, contentSize] = useMeasure({ scroll: false });
  const [isOpen, setIsOpen] = useState(isInitiallyOpen);
  const [realHeight, setRealHeight] = useState(isInitiallyOpen ? MIN_OPEN_HEIGHT : CLOSED_HEIGHT);
  const [{ y }, set] = useSpring(() => ({ y: isInitiallyOpen ? MIN_OPEN_HEIGHT : CLOSED_HEIGHT }));

  // const debouncedSetRealHeight = useMemo(() => debounce(setRealHeight, 100), [setRealHeight]);

  const snapTo = (newY: number, canceled: boolean) => {
    const correctedY = newY > maxHeight - MAX_HEIGHT_MARGIN ? maxHeight : newY;
    setIsOpen(correctedY > CLOSED_HEIGHT);
    setRealHeight(correctedY);
    // debouncedSetRealHeight(correctedY);
    // debouncedSetRealHeight.flush();
    set({ y: correctedY, config: canceled ? config.gentle : config.stiff });
  };
  const moveTo = (newY: number) => {
    const clampedY = Math.min(maxHeight, Math.max(newY, CLOSED_HEIGHT));
    setIsOpen(newY > CLOSED_HEIGHT);
    setRealHeight(clampedY);
    // debouncedSetRealHeight(Math.max(newY, CLOSED_HEIGHT));
    set({
      y: clampedY,
      immediate: true,
      config: config.stiff,
    });
  };
  const bind = useDrag(
    ({ velocity: [, vy], movement: [, my], memo = y.get(), last, canceled, event, tap }) => {
      event.preventDefault();
      if (tap && !isOpen) {
        snapTo(MIN_OPEN_HEIGHT, canceled);
        return;
      }

      const newY = Math.min(memo - my, maxHeight);
      // when the user releases the sheet, we check whether it passed
      // the treshold for it to close, or if we reset it to its open position
      if (last) {
        if (newY < MIN_OPEN_HEIGHT * 0.5 || vy < -0.5) {
          snapTo(CLOSED_HEIGHT, canceled);
          return;
        }
        if (vy > 2) {
          snapTo(maxHeight, canceled);
          return;
        }
        if (newY <= MIN_OPEN_HEIGHT) {
          snapTo(MIN_OPEN_HEIGHT, canceled);
          return;
        }
        if (newY > maxHeight - MAX_HEIGHT_MARGIN) {
          snapTo(maxHeight, canceled);
          return;
        }
        moveTo(Math.round(newY));
      } else {
        // when the user keeps dragging, we just move the sheet according to
        // the cursor position
        moveTo(newY);
      }
      return memo;
    },
    {},
  );

  return (
    <animated.div
      className="info-sheet"
      ref={contentRef}
      style={{
        height: y.to((currentY) => `${currentY}px`),
      }}
    >
      <div
        {...bind()}
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          touchAction: 'none',
        }}
      >
        <div className="info-sheet-throbber" />
      </div>
      {isOpen && (
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: realHeight - CLOSED_HEIGHT,
              overflow: 'hidden',
            }}
            onTouchMove={(ev) => {
              ev.stopPropagation();
            }}
          >
            {contentSize.width > 0 &&
              realHeight > CLOSED_HEIGHT &&
              children(contentSize.width, realHeight - CLOSED_HEIGHT)}
          </div>
        </div>
      )}
    </animated.div>
  );
}
